import { carrierDataSelectors } from 'wanderu-ducks-carriers';

import NAMESPACE from './types';
import { liveCheckCachedTripAction } from '../actions/liveCheckCachedTrip';
import { liveCheckTripAction } from '../actions/liveCheckTrip';
import { serializeLiveCheckTripParams as serializeParams } from '../actions/utils';
import {
    ERROR,
    NOT_FOUND,
    SUCCESS_PRICE_UP,
    SUCCESS_PRICE_DOWN,
    SUCCESS_UNAVAILABLE,
    SUCCESS_NO_CHANGE,
} from '../constants';
import SLICE_NAMESPACE from '../types';

function selectData(state) {
    return state[SLICE_NAMESPACE][NAMESPACE];
}

function selectAllCheckoutData(state) {
    return selectData(state).checkoutData;
}

function selectLiveChecks(state) {
    return selectData(state).liveChecks;
}

function selectTripCheckoutData(appState, lcParams) {
    const lcId = typeof lcParams === 'string'
        ? lcParams
        : serializeParams(lcParams);
    return selectAllCheckoutData(appState)[lcId];
}

function selectTripLiveCheck(appState, lcParams) {
    const lcId = typeof lcParams === 'string'
        ? lcParams
        : serializeParams(lcParams);

    return selectLiveChecks(appState)[lcId];
}

function selectTripNativeBooking(appState, lcParams) {
    const lcId = typeof lcParams === 'string'
        ? lcParams
        : serializeParams(lcParams);

    const checkoutData = selectTripCheckoutData(appState, lcId) || {};
    return checkoutData.nativeBookingEnabled;
}

function selectLiveCheckPrice(appState, lcParams) {
    const liveCheck = selectTripLiveCheck(appState, lcParams);
    if (!liveCheck) {
        return undefined;
    }
    return liveCheck.tripData.pricing;
}

function selectAvailableFares(appState, lcParams) {
    const liveCheck = selectTripLiveCheck(appState, lcParams);
    if (!liveCheck) {
        return undefined;
    }
    return liveCheck.tripData.available_fares_v2;
}

function selectPreviousLiveCheckPrice(appState, tripId) {
    const pricingHistory = selectData(appState).pricingHistory[tripId] || [];
    return pricingHistory[1];
}

// Round a price to whole cents before comparing. Prices are derived by
// dividing a total by a seat count (e.g. 64 / 3), which produces repeating
// binary fractions, so a direct `>`/`<` comparison flags a price change even
// when the amounts the customer sees are identical (the "$64.00 → $64.00"
// modal). Comparing at cent precision matches what is actually displayed.
function toCents(price) {
    return Math.round(price * 100);
}

function mapLiveCheckMessageData(tripLCData) {
    if (!tripLCData || !tripLCData.length) {
        return undefined;
    }

    return tripLCData.reduce((messageData, data) => {
        const {
            error = {},
            tripId,
            latestLiveCheck,
            previousPricingObj,
            carrierCurrency,
        } = data;
        const messageDataCopy = { ...messageData };

        if (error.code === 'LIVE_CHECK_TRIP_NOT_FOUND') {
            messageDataCopy.notFound.push(tripId);
        } else if (!latestLiveCheck) {
            messageDataCopy.error.push(tripId);
        } else {
            const {
                message,
                nativeBookingEnabled,
                tripData,
                liveCheckArgs,
                priceAcknowledged,
            } = latestLiveCheck;
            const {
                pricing,
                selected_fare: selectedFare,
            } = tripData;
            const {
                pricing: prevPricing,
                seats: prevSeats,
                selectedFare: prevSelectedFare,
            } = previousPricingObj;

            let latestPrice;
            let previousPrice;

            if (pricing?.[carrierCurrency]?.price) {
                latestPrice = pricing[carrierCurrency].price / liveCheckArgs.seats;
            }

            if (prevPricing?.[carrierCurrency]?.price) {
                previousPrice = prevPricing[carrierCurrency].price / prevSeats;
            }

            messageDataCopy.prices.current[tripId] = latestPrice;
            messageDataCopy.prices.previous[tripId] = previousPrice;

            if (message === 'unavailable') {
                messageDataCopy.unavailable.push(tripId);
                messageDataCopy.showLiveCheckMessage = true;
            } else if (message === 'error') {
                messageDataCopy.error.push(tripId);
                messageDataCopy.showLiveCheckMessage = true;
            } else if (
                prevSelectedFare === selectedFare
                && previousPrice !== undefined
                && toCents(latestPrice) > toCents(previousPrice)
            ) {
                messageDataCopy.priceUp.push(tripId);
                messageDataCopy.showLiveCheckMessage = !priceAcknowledged;
            } else if (
                prevSelectedFare === selectedFare
                && previousPrice !== undefined
                && toCents(latestPrice) < toCents(previousPrice)
            ) {
                messageDataCopy.priceDown.push(tripId);
                messageDataCopy.showLiveCheckMessage = true;
            } else {
                messageDataCopy.available.push(tripId);
            }

            if (nativeBookingEnabled) {
                messageDataCopy.nativeBookingEnabled.push(tripId);
            }
        }

        return messageDataCopy;
    }, {
        available: [],
        error: [],
        nativeBookingEnabled: [],
        notFound: [],
        priceDown: [],
        prices: { current: {}, previous: {} },
        priceUp: [],
        showLiveCheckMessage: false,
        total: tripLCData.length,
        unavailable: [],
    });
}

function selectLiveCheckData(
    appState,
    lcParamsArr,
    originalPricingObj = {},
    originalSeatsObj = {},
) {
    const liveCheckDataSets = lcParamsArr.map((lcParams) => {
        const { tripId } = lcParams;
        const latestLiveCheck = selectTripLiveCheck(appState, lcParams);
        const error = liveCheckTripAction.selectError(appState, tripId);

        let carrierCurrency;
        let availableFares;
        if (latestLiveCheck) {
            const {
                carrier: carrierCode,
                available_fares_v2: fares = [],
            } = latestLiveCheck.tripData;
            const carrierData = carrierDataSelectors.selectCarrierData(appState, carrierCode);
            carrierCurrency = carrierData?.preferredCurrency;
            availableFares = fares;
        }

        const prevLCPricing = selectPreviousLiveCheckPrice(appState, lcParams.tripId);
        // Normalize the original (search) price by the trip's OWN seat count,
        // mirroring how the display selector normalizes (`normalizedSeats =
        // tripSeats > 1 ? tripSeats : 1` in wanderu-ducks-trips). The search
        // price is only a party total when the trip carries `seats > 1`; for a
        // multi-pax search served from the single-passenger scrape cache
        // (pservs skips multi-pax cache export) the trip arrives PER-PERSON
        // with `seats: '1'`. Dividing that per-person price by the requested
        // passenger count (the old `lcParams.seats` behaviour) double-discounts
        // it and fabricates a price change while the modal — which scales the
        // display by pax — shows identical totals ("$96.00 → $96.00").
        const originalTripSeats = Number.parseInt(originalSeatsObj[tripId], 10);
        const normalizedOriginalSeats = originalTripSeats > 1 ? originalTripSeats : 1;
        const originalPricing = {
            pricing: originalPricingObj[tripId],
            seats: normalizedOriginalSeats,
        };
        const previousPricingObj = prevLCPricing || originalPricing;
        return {
            availableFares,
            carrierCurrency,
            error,
            latestLiveCheck,
            previousPricingObj,
            tripId,
        };
    });

    return mapLiveCheckMessageData(liveCheckDataSets);
}

function selectLiveCheckStatusData(appState, lcParams, originalPricing, originalSeats) {
    const { tripId } = lcParams;
    const liveCheckMessageData = selectLiveCheckData(
        appState,
        [lcParams],
        { [tripId]: originalPricing },
        { [tripId]: originalSeats },
    );

    const price = liveCheckMessageData?.prices?.current?.[tripId];
    const originalPrice = liveCheckMessageData?.prices?.previous?.[tripId];
    let liveCheckStatus = null;

    if (liveCheckMessageData.error.includes(tripId)) {
        liveCheckStatus = ERROR;
    } else if (liveCheckMessageData.notFound.includes(tripId)) {
        liveCheckStatus = NOT_FOUND;
    } else if (liveCheckMessageData.priceUp.includes(tripId)) {
        liveCheckStatus = SUCCESS_PRICE_UP;
    } else if (liveCheckMessageData.priceDown.includes(tripId)) {
        liveCheckStatus = SUCCESS_PRICE_DOWN;
    } else if (liveCheckMessageData.unavailable.includes(tripId)) {
        liveCheckStatus = SUCCESS_UNAVAILABLE;
    } else {
        liveCheckStatus = SUCCESS_NO_CHANGE;
    }

    return ({
        originalPrice,
        price,
        priceDifference: price && originalPrice
            ? price - originalPrice
            : undefined,
        status: liveCheckStatus,
        tripId,
    });
}

function selectAllLiveCheckStatusData(
    appState,
    lcParamsArr,
    originalPricingObj,
    originalSeatsObj = {},
) {
    return lcParamsArr.map((lcParams) => {
        const originalPricing = originalPricingObj[lcParams.tripId];
        const originalSeats = originalSeatsObj[lcParams.tripId];
        return selectLiveCheckStatusData(appState, lcParams, originalPricing, originalSeats);
    });
}

function selectLiveCheckSummaryStatus(
    appState,
    lcParamsArr,
    originalPricingObj,
    originalSeatsObj = {},
) {
    const allLiveCheckStatusData = selectAllLiveCheckStatusData(
        appState,
        lcParamsArr,
        originalPricingObj,
        originalSeatsObj,
    );

    if (allLiveCheckStatusData.some((liveCheckData) => liveCheckData.status === ERROR)) {
        return ERROR;
    }

    if (allLiveCheckStatusData.some((liveCheckData) => liveCheckData.status === NOT_FOUND)) {
        return NOT_FOUND;
    }

    if (allLiveCheckStatusData
        .some((liveCheckData) => liveCheckData.status === SUCCESS_UNAVAILABLE)) {
        return SUCCESS_UNAVAILABLE;
    }

    const totalPriceDifference = allLiveCheckStatusData
        .reduce((accumulator, { priceDifference = 0 }) => ( // priceDifference needs a default value, because if we try to do operations on undefined, it will error
            accumulator + priceDifference
        ), 0);

    // Compare at cent precision so sub-cent float drift in the summed
    // difference doesn't register as a change (see toCents).
    const totalPriceDifferenceInCents = toCents(totalPriceDifference);

    if (totalPriceDifferenceInCents > 0) {
        return SUCCESS_PRICE_UP;
    }

    if (totalPriceDifferenceInCents < 0) {
        return SUCCESS_PRICE_DOWN;
    }

    return SUCCESS_NO_CHANGE;
}

function selectLiveChecksInProgress(appState, lcParamsArr, opts = {}) {
    const { cached = false } = opts;
    const actionDuck = cached
        ? liveCheckCachedTripAction
        : liveCheckTripAction;
    return lcParamsArr.some((lcParams) => actionDuck.selectWorking(appState, lcParams));
}

function selectLiveChecksCompleted(appState, lcParamsArr, opts = {}) {
    const { cached = false } = opts;
    const actionDuck = cached
        ? liveCheckCachedTripAction
        : liveCheckTripAction;
    return lcParamsArr.every((lcParams) => {
        const hasErrored = !!actionDuck.selectError(appState, lcParams);
        const hasSucceeded = actionDuck.selectSuccess(appState, lcParams);
        return hasErrored || hasSucceeded;
    });
}

function selectLiveChecksSuccess(appState, lcParamsArr, opts = {}) {
    const { cached = false } = opts;
    const actionDuck = cached
        ? liveCheckCachedTripAction
        : liveCheckTripAction;
    return lcParamsArr.every((lcParams) => actionDuck.selectSuccess(appState, lcParams));
}

function selectLiveChecksErrored(appState, lcParamsArr, opts = {}) {
    const { cached = false } = opts;
    const actionDuck = cached
        ? liveCheckCachedTripAction
        : liveCheckTripAction;
    return lcParamsArr.every((lcParams) => actionDuck.selectError(appState, lcParams));
}

export {
    selectAvailableFares,
    selectLiveCheckData,
    selectLiveCheckPrice,
    selectLiveCheckStatusData,
    selectLiveCheckSummaryStatus,
    selectLiveChecks,
    selectLiveChecksCompleted,
    selectLiveChecksErrored,
    selectLiveChecksInProgress,
    selectLiveChecksSuccess,
    selectTripCheckoutData,
    selectTripLiveCheck,
    selectTripNativeBooking,
};
